import L from "leaflet";

export const FEET_TO_METERS = 0.3048;

// Closest point on the drawn route. "along" is meters from the route start.
export function projectOntoRoute(lat, lng, routePts) {
  if (!routePts || routePts.length < 2) return null;
  const p = L.latLng(lat, lng);
  let total = 0;
  let best = null;
  for (let i = 0; i < routePts.length - 1; i++) {
    const a = routePts[i];
    const b = routePts[i + 1];
    const segLen = a.distanceTo(b);
    const dx = b.lat - a.lat;
    const dy = b.lng - a.lng;
    const len2 = dx * dx + dy * dy;
    const t =
      len2 > 0
        ? Math.max(0, Math.min(1, ((p.lat - a.lat) * dx + (p.lng - a.lng) * dy) / len2))
        : 0;
    const proj = L.latLng(a.lat + t * dx, a.lng + t * dy);
    const perp = p.distanceTo(proj);
    if (!best || perp < best.perp) {
      best = { along: total + t * segLen, perp, lat: proj.lat, lng: proj.lng };
    }
    total += segLen;
  }
  return best;
}

export function pointAtAlong(routePts, along) {
  let remaining = along;
  for (let i = 0; i < routePts.length - 1; i++) {
    const a = routePts[i];
    const b = routePts[i + 1];
    const segLen = a.distanceTo(b);
    if (remaining <= segLen || i === routePts.length - 2) {
      const t = segLen > 0 ? Math.max(0, Math.min(1, remaining / segLen)) : 0;
      return { lat: a.lat + t * (b.lat - a.lat), lng: a.lng + t * (b.lng - a.lng) };
    }
    remaining -= segLen;
  }
  return { lat: routePts[0].lat, lng: routePts[0].lng };
}

// "145 Lee Ave" for one house, "145–171 Lee Ave" for a range on the main street.
export function defaultStopName(items) {
  const byStreet = {};
  items.forEach((it) => {
    const street = (it.street || "").trim();
    if (!street) return;
    const key = street.toLowerCase();
    if (!byStreet[key]) byStreet[key] = { street, houses: [] };
    byStreet[key].houses.push((it.house || "").trim());
  });
  const top = Object.values(byStreet).sort((a, b) => b.houses.length - a.houses.length)[0];
  if (!top) return "";
  const nums = top.houses
    .map((h) => parseInt(h, 10))
    .filter((n) => !isNaN(n))
    .sort((a, b) => a - b);
  if (!nums.length) return top.street;
  if (nums[0] === nums[nums.length - 1]) return nums[0] + " " + top.street;
  return nums[0] + "–" + nums[nums.length - 1] + " " + top.street;
}

// Walks the route in order and starts a new stop whenever the next student is
// more than groupFeet past the first student of the current stop.
// items: [{ studentID, lat, lng, house, street }]
export function groupAlongRoute(items, routePts, groupFeet) {
  const groupMeters = Math.max(1, groupFeet || 100) * FEET_TO_METERS;
  const projected = items
    .map((it) => ({ it, proj: projectOntoRoute(it.lat, it.lng, routePts) }))
    .filter((x) => x.proj)
    .sort((a, b) => a.proj.along - b.proj.along);

  const groups = [];
  let current = null;
  projected.forEach((x) => {
    if (!current || x.proj.along - current.start > groupMeters) {
      current = { start: x.proj.along, end: x.proj.along, members: [] };
      groups.push(current);
    }
    current.end = x.proj.along;
    current.members.push(x.it);
  });

  return groups.map((g) => {
    const along = (g.start + g.end) / 2;
    const pt = pointAtAlong(routePts, along);
    return {
      lat: pt.lat,
      lng: pt.lng,
      along,
      name: defaultStopName(g.members),
      studentIDs: g.members.map((m) => m.studentID),
    };
  });
}

// Renumbers 1..n by position along the route; stops that can't be projected keep
// their relative order at the end.
export function numberStopsAlongRoute(stops, routePts) {
  const keyed = stops.map((s, i) => {
    const proj = s.lat != null && s.lng != null ? projectOntoRoute(s.lat, s.lng, routePts) : null;
    return { s, along: proj ? proj.along : Infinity, i };
  });
  keyed.sort((a, b) => a.along - b.along || a.i - b.i);
  keyed.forEach((k, idx) => {
    k.s.stopNumber = idx + 1;
  });
  return keyed.map((k) => k.s);
}

// Inserts a new stop by its route position without disturbing the order the user
// already set for the other stops.
export function insertStopInOrder(stops, newStop, routePts) {
  const ordered = stops.slice().sort((a, b) => a.stopNumber - b.stopNumber);
  const newProj = projectOntoRoute(newStop.lat, newStop.lng, routePts);
  let index = ordered.length;
  if (newProj) {
    const next = ordered.findIndex((s) => {
      const p = s.lat != null ? projectOntoRoute(s.lat, s.lng, routePts) : null;
      return p && p.along > newProj.along;
    });
    if (next !== -1) index = next;
  }
  ordered.splice(index, 0, newStop);
  ordered.forEach((s, i) => {
    s.stopNumber = i + 1;
  });
  return ordered;
}

export function stopIcon(number, color, highlighted) {
  const size = highlighted ? 30 : 24;
  const html =
    '<div style="width:' + size + "px;height:" + size + "px;border-radius:6px;" +
    "background:" + color + ";color:#fff;border:2px solid #fff;" +
    "box-shadow:0 1px 4px rgba(0,0,0,.45);display:flex;align-items:center;" +
    "justify-content:center;font:700 " + (highlighted ? 13 : 12) + "px/1 Roboto,Arial,sans-serif;" +
    '">' + number + "</div>";
  return L.divIcon({
    html,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: "",
  });
}
